/* cart-redesign: behaviour for the redesigned Be Yours cart drawer.
 * - <cart-recs-slider>: scroll-snap slider with segmented progress + prev/next
 * - Line item pending/loading state while the theme's cart-items updates
 * - Keeps focus inside the drawer when #mini-cart is re-rendered
 * - Keyboard support for <drawer-close-button>
 * Cart requests/re-rendering stay in the theme's cart.js / cart-drawer.js.
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (!customElements.get('cart-recs-slider')) {
    class CartRecsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.cdr__track');
        if (!this.track) return;

        this.dashes = Array.from(this.querySelectorAll('.cdr__dash'));
        this.prevButton = this.querySelector('[name="previous"]');
        this.nextButton = this.querySelector('[name="next"]');

        this.onScroll = this.onScroll.bind(this);
        this.onPrev = () => this.go(-1);
        this.onNext = () => this.go(1);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.prevButton?.addEventListener('click', this.onPrev);
        this.nextButton?.addEventListener('click', this.onNext);

        // Drawer opens after this renders (width 0 while closed) — recalc when it gets a size.
        this.resizeObserver = new ResizeObserver(this.onScroll);
        this.resizeObserver.observe(this.track);

        this.update();
      }

      disconnectedCallback() {
        if (!this.track) return;
        this.track.removeEventListener('scroll', this.onScroll);
        this.prevButton?.removeEventListener('click', this.onPrev);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.resizeObserver?.disconnect();
        if (this.frame) cancelAnimationFrame(this.frame);
      }

      get isRtl() {
        return getComputedStyle(this.track).direction === 'rtl';
      }

      get step() {
        const slide = this.track.firstElementChild;
        if (!slide) return 0;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return slide.getBoundingClientRect().width + gap;
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      }

      update() {
        const maxScroll = this.track.scrollWidth - this.track.clientWidth;
        const position = Math.abs(this.track.scrollLeft);
        const step = this.step;
        const isStatic = maxScroll <= 1 || step === 0;

        this.classList.toggle('cdr__slider--static', isStatic);
        if (isStatic) return;

        const atEnd = position >= maxScroll - 1;
        const index = atEnd ? this.dashes.length - 1 : Math.round(position / step);
        this.dashes.forEach((dash, i) => dash.classList.toggle('is-active', i === index));

        this.setDisabled(this.prevButton, position <= 1, this.nextButton);
        this.setDisabled(this.nextButton, atEnd, this.prevButton);
      }

      // Moving focus off a button before disabling it — otherwise focus drops to <body>
      // and the theme's MenuDrawer focusout handler closes the drawer.
      setDisabled(button, disabled, fallback) {
        if (!button || button.disabled === disabled) return;
        if (disabled && document.activeElement === button) {
          (fallback && !fallback.disabled ? fallback : this.track).focus();
        }
        button.disabled = disabled;
      }

      go(direction) {
        const step = this.step;
        if (!step) return;
        const perPage = Math.max(1, Math.floor((this.track.clientWidth + 1) / step));
        this.track.scrollBy({
          left: direction * step * perPage * (this.isRtl ? -1 : 1),
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      }
    }

    customElements.define('cart-recs-slider', CartRecsSlider);
  }

  /* Line item loading state. cart-items debounces changes by 300ms, so the row only
     becomes non-interactive once the request is actually about to fire. */
  const LOADING_DELAY = 300;
  const LOADING_TIMEOUT = 10000;

  const setRowPending = (row) => {
    if (!row) return;
    row.classList.add('is-pending');
    clearTimeout(row.cdrLoadingTimer);
    clearTimeout(row.cdrResetTimer);
    row.cdrLoadingTimer = setTimeout(() => {
      row.classList.add('is-loading');
      row.setAttribute('aria-busy', 'true');
    }, LOADING_DELAY);
    // Safety net if the request fails without a re-render.
    row.cdrResetTimer = setTimeout(() => clearRow(row), LOADING_TIMEOUT);
  };

  const clearRow = (row) => {
    clearTimeout(row.cdrLoadingTimer);
    clearTimeout(row.cdrResetTimer);
    row.classList.remove('is-pending', 'is-loading');
    row.removeAttribute('aria-busy');
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches('#mini-cart cart-items .quantity__input')) return;
    setRowPending(event.target.closest('li'));
  });

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest('#mini-cart cart-remove-button');
    if (removeButton) setRowPending(removeButton.closest('li'));
  });

  document.addEventListener('cart:error', () => {
    document.querySelectorAll('#mini-cart cart-items li.is-pending').forEach(clearRow);
  });

  /* Add-to-cart errors from the recommendations slider (theme's <add-to-cart> dispatches this). */
  document.addEventListener('ajaxProduct:error', (event) => {
    const error = document.querySelector('#mini-cart .cdr__error');
    if (error) error.textContent = event.detail?.errorMessage || theme.cartStrings?.error || '';
  });

  /* Keyboard support for the close control (Enter / Space). */
  document.addEventListener('keydown', (event) => {
    const closeButton = event.target.closest?.('#mini-cart drawer-close-button');
    if (!closeButton || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    closeButton.click();
  });

  /* #mini-cart innerHTML is replaced on every cart change. If the focused element was
     removed, move focus to the drawer heading so focus stays trapped in the open drawer. */
  const miniCart = document.getElementById('mini-cart');
  if (miniCart) {
    new MutationObserver(() => {
      const details = miniCart.closest('details');
      if (!details || !details.hasAttribute('open')) return;
      if (details.contains(document.activeElement)) return;
      miniCart.querySelector('#CartDrawer-Title')?.focus({ preventScroll: true });
    }).observe(miniCart, { childList: true });
  }
})();
